import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

export const INLINE_CITATION_CLASS = "note-reference-citation";

export function getInlineCitationLabel({ citationLabel, referenceNo } = {}) {
    return citationLabel || (referenceNo ? `[${referenceNo}]` : "[ref]");
}

function normalizeText(value) {
    if (Array.isArray(value)) return value.filter(Boolean).join(" ");
    return value || "";
}

function getReferenceBibJson(reference) {
    return reference?.bib_json || reference?.reference_bib_json || reference?.reference?.bib_json || {};
}

function getReferenceTitle(reference) {
    const bibJson = getReferenceBibJson(reference);
    return (
        normalizeText(bibJson.title) ||
        reference?.display_name ||
        reference?.reference_display_name ||
        reference?.title ||
        "제목 없음"
    );
}

const CitationText = styled.span.attrs((props) => ({
    className: [INLINE_CITATION_CLASS, props.className].filter(Boolean).join(" "),
    "data-note-reference-citation": "true",
}))`
    border-radius: 4px;
    padding: 1px 4px;
    background: var(--color-QuoteBox);
    color: var(--color-SubBlack);
    font-size: 0.95em;
    cursor: ${(props) => (props.$hasReference ? "help" : "default")};
`;

const LayerBox = styled.div`
    position: fixed;
    top: ${(props) => props.$top}px;
    left: ${(props) => props.$left}px;
    z-index: 120;
    width: 320px;
    max-width: calc(100vw - 24px);
    padding: 12px;
    border: 1px solid var(--color-Line);
    border-radius: 6px;
    background: var(--color-White);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
    color: var(--color-Black);
    white-space: normal;
    pointer-events: auto;
    user-select: text;
`;

const LayerTitle = styled.div`
    margin-bottom: 8px;
    color: var(--color-Black);
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.45;
`;

const LayerMeta = styled.div`
    color: var(--color-SubBlack);
    font-size: 1.15rem;
    line-height: 1.5;
`;

function calculateLayerPosition(targetRect, layerRect) {
    const space = 8;
    const viewportPadding = 12;
    const layerWidth = layerRect?.width || 320;
    const layerHeight = layerRect?.height || 96;

    let left = targetRect.left;
    let top = targetRect.bottom + space;

    if (left + layerWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - layerWidth - viewportPadding;
    }
    if (left < viewportPadding) {
        left = viewportPadding;
    }
    if (top + layerHeight > window.innerHeight - viewportPadding) {
        top = targetRect.top - layerHeight - space;
    }
    if (top < viewportPadding) {
        top = viewportPadding;
    }
    return { left, top };
}

function InlineCitation({ children, citationLabel, getReference, label, reference, referenceNo, ...props }) {
    const targetRef = useRef(null);
    const layerRef = useRef(null);
    const hideTimerRef = useRef(null);
    const [layerPosition, setLayerPosition] = useState(null);
    const [activeReference, setActiveReference] = useState(null);
    const hasReference = Boolean(reference || getReference);
    const citationText = label || getInlineCitationLabel({ citationLabel: citationLabel || children, referenceNo });

    const showLayer = useCallback(() => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        const nextReference = getReference?.() || reference;
        if (!nextReference || !targetRef.current) return;
        const targetRect = targetRef.current.getBoundingClientRect();
        const layerRect = layerRef.current?.getBoundingClientRect();
        setActiveReference(nextReference);
        setLayerPosition(calculateLayerPosition(targetRect, layerRect));
    }, [getReference, reference]);

    const hideLayer = useCallback(() => {
        hideTimerRef.current = setTimeout(() => {
            setLayerPosition(null);
            setActiveReference(null);
        }, 120);
    }, []);

    useEffect(
        () => () => {
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        },
        [],
    );

    const layerReference = activeReference || reference;

    return (
        <>
            <CitationText
                {...props}
                ref={targetRef}
                $hasReference={hasReference}
                onBlur={(event) => {
                    setLayerPosition(null);
                    setActiveReference(null);
                    props.onBlur?.(event);
                }}
                onFocus={(event) => {
                    showLayer();
                    props.onFocus?.(event);
                }}
                onMouseEnter={(event) => {
                    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
                    showLayer();
                    props.onMouseEnter?.(event);
                }}
                onMouseLeave={(event) => {
                    hideLayer();
                    props.onMouseLeave?.(event);
                }}
            >
                {citationText}
            </CitationText>
            {layerReference && layerPosition && (
                <LayerBox
                    ref={layerRef}
                    $left={layerPosition.left}
                    $top={layerPosition.top}
                    role="tooltip"
                    onMouseEnter={() => {
                        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
                    }}
                    onMouseLeave={hideLayer}
                >
                    <LayerTitle>{getReferenceTitle(layerReference)}</LayerTitle>
                    {layerReference?.bibliography_text && <LayerMeta>{layerReference.bibliography_text}</LayerMeta>}
                </LayerBox>
            )}
        </>
    );
}

function renderInlineCitationNode(node) {
    if (typeof node === "string") {
        return node.split(/(\[#\d+\])/g).map((part, index) => {
            const match = part.match(/^\[#(\d+)\]$/);
            if (!match) {
                return part;
            }
            return <InlineCitation key={`${part}-${index}`} label={`[${match[1]}]`} />;
        });
    }
    if (!React.isValidElement(node)) {
        return node;
    }
    return React.cloneElement(node, {
        ...node.props,
        children: React.Children.map(node.props.children, renderInlineCitationNode),
    });
}

export function renderInlineCitationChildren(children) {
    return React.Children.map(children, renderInlineCitationNode);
}

export const inlineCitationMarkdownComponents = {
    p: ({ children }) => <p>{renderInlineCitationChildren(children)}</p>,
    li: ({ children }) => <li>{renderInlineCitationChildren(children)}</li>,
    h1: ({ children }) => <h1>{renderInlineCitationChildren(children)}</h1>,
    h2: ({ children }) => <h2>{renderInlineCitationChildren(children)}</h2>,
    h3: ({ children }) => <h3>{renderInlineCitationChildren(children)}</h3>,
    h4: ({ children }) => <h4>{renderInlineCitationChildren(children)}</h4>,
    h5: ({ children }) => <h5>{renderInlineCitationChildren(children)}</h5>,
    h6: ({ children }) => <h6>{renderInlineCitationChildren(children)}</h6>,
    td: ({ children }) => <td>{renderInlineCitationChildren(children)}</td>,
    th: ({ children }) => <th>{renderInlineCitationChildren(children)}</th>,
};

export default InlineCitation;
